import React from "react";
import { CheckIcon } from "@heroicons/react/outline";
import { cn } from "./cn";
import ProgressBar from "./ProgressBar";
import { ABUNDO_MARK_URL } from './brandAssets';

/**
 * Bun wizard header (welcome flow): progress bar + autosave state + section
 * pills. Spec: Component Library "Wizard & onboarding". Pill states: done =
 * mist-deep with a check, current = ink, upcoming = mist + stone.
 *
 *   <WizardHeader progress={44} savedLabel="Draft saved"
 *     action={<a href="...">Save & exit</a>}
 *     sections={[
 *       { id: "why", label: "What Brings You Here", state: "done" },
 *       { id: "income", label: "Income", state: "current" },
 *       { id: "spending", label: "Spending" },
 *     ]}
 *     onSectionClick={(id) => ...} // done sections only
 *   />
 */
export default function WizardHeader({
  title,
  status,
  progress = 0,
  savedLabel,
  saving = false,
  action,
  sections = [],
  onSectionClick,
  allowSectionNavigation = false,
  navigationLabel = 'Onboarding sections',
  className,
}) {
  return (
    <div className={cn("font-instrument", className)}>
      <div className="flex flex-wrap items-center gap-[10px]">
        {title ? <>
          <img src={ABUNDO_MARK_URL} alt="" aria-hidden="true" className="h-7 w-7 shrink-0 object-contain" />
          <span className="min-w-0 flex-1 text-bun-body font-semibold text-bun-text">{title}</span>
          {status}
        </> : <ProgressBar value={progress} size="sm" className="flex-1" />}
        {savedLabel && (
          <span role="status" aria-busy={saving} className="inline-flex items-center gap-[5px] text-bun-meta font-book text-bun-stone">
            {!saving && <CheckIcon aria-hidden="true" className="w-3 h-3" />}
            {savedLabel}
          </span>
        )}
        {action}
      </div>
      {title && <ProgressBar value={progress} size="sm" aria-label={`${title} progress`} className="mt-[18px]" />}
      {sections.length > 0 && (
        <nav aria-label={navigationLabel} className={cn("flex flex-wrap gap-[6px]", title ? 'mt-[14px]' : 'mt-[10px]')}>
          {sections.map((section) => {
            const state = section.state || "upcoming";
            const clickable = (allowSectionNavigation || state === "done") && onSectionClick;
            const Tag = clickable ? "button" : "span";
            return (
              <Tag
                key={section.id ?? section.label}
                aria-current={state === 'current' ? 'step' : undefined}
                {...(clickable && {
                  type: "button",
                  onClick: () => onSectionClick(section.id ?? section.label),
                })}
                className={cn(
                  "inline-flex min-h-11 sm:min-h-0 max-w-full items-center gap-1 text-bun-label tracking-normal font-semibold rounded-full px-[10px] py-1 border-0",
                  state === "current" && "bg-bun-ink text-bun-on-ink",
                  state === "done" && "bg-bun-mist-deep text-bun-text",
                  state === "upcoming" && "bg-bun-mist text-bun-stone",
                  clickable &&
                    "cursor-pointer focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue"
                )}
              >
                {state === "done" && <CheckIcon className="w-[10px] h-[10px]" />}
                {section.label}
              </Tag>
            );
          })}
        </nav>
      )}
    </div>
  );
}
